// InfoCongesEmploye.jsx – Version profil employé (avec id pré-rempli)
import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, faTimes, faCalendarAlt, faComment, faBriefcase, 
  faExclamationTriangle, faCheckCircle 
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

const API_CONGE = `${API_CONFIG2.apiUrl}conge/api_conge.php`;

// ============================================================
// HOOK PERSONNALISÉ – Gestion des congés (sans liste employés)
// ============================================================
function useConge() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const submitConge = useCallback(async (formData, userId) => {
    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      const payload = {
        ...formData,
        employeId: parseInt(formData.employeId),
        userId: userId || null
      };
      const res = await fetch(`${API_CONGE}?action=create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.status === 'success') {
        setSuccess(true);
        return data;
      } else {
        throw new Error(data.message || 'Erreur lors de l\'enregistrement');
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  return {
    submitting,
    error,
    success,
    submitConge,
    reset,
  };
}

// ============================================================
// COMPOSANT PRINCIPAL – InfoCongesEmploye
// ============================================================
const InfoCongesEmploye = ({ userId, employeId, onSuccess, onError }) => {
  const { submitting, error, success, submitConge, reset } = useConge();

  const [formData, setFormData] = useState({
    employeId: employeId || '',
    type: '',
    dateDebut: '',
    dateFin: '',
    motif: ''
  });
  const [validationError, setValidationError] = useState(null);

  // Réinitialiser le formulaire après succès
  useEffect(() => {
    if (success) {
      setFormData(prev => ({
        ...prev,
        type: '',
        dateDebut: '',
        dateFin: '',
        motif: ''
      }));
      if (onSuccess) onSuccess();
    }
  }, [success, onSuccess]);

  // Mettre à jour l'employeId si la prop change
  useEffect(() => {
    if (employeId) {
      setFormData(prev => ({ ...prev, employeId }));
    }
  }, [employeId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    reset();
    setValidationError(null);
  };

  const validate = () => {
    const { type, dateDebut, dateFin, motif } = formData;
    if (!type || !dateDebut || !dateFin || !motif) {
      return 'Tous les champs sont obligatoires';
    }
    const start = new Date(dateDebut);
    const end = new Date(dateFin);
    if (end < start) {
      return 'La date de fin doit être postérieure ou égale à la date de début';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError(null);
    try {
      await submitConge(formData, userId);
    } catch (err) {
      if (onError) onError(err);
    }
  };

  // Si l'employeId n'est pas fourni, on affiche un message
  if (!employeId) {
    return (
      <div className="demande-conge-container text-center py-4">
        <p className="text-secondary">Aucun employé sélectionné. Veuillez spécifier un employé.</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* ============================================================
           STYLES GLASSMORPHISM – Demande de congé (profil employé)
           ============================================================ */
        .demande-conge-container {
          background: rgba(20, 40, 80, 0.55);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(59, 130, 246, 0.15);
          border-radius: 24px;
          padding: clamp(1.2rem, 2vw, 2rem);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
          color: #e0e7ff;
          transition: all 0.25s ease;
          max-width: 100%;
          margin: 0 auto;
        }
        .demande-conge-container:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 48px rgba(59, 130, 246, 0.08);
        }

        .demande-conge-container .form-label {
          color: #94a3b8;
          font-weight: 500;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .demande-conge-container .form-control,
        .demande-conge-container .form-select {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.06);
          color: #e0e7ff;
          border-radius: 12px;
          padding: 10px 14px;
          transition: border 0.2s, box-shadow 0.2s;
          font-size: 0.95rem;
        }
        .demande-conge-container .form-control:focus,
        .demande-conge-container .form-select:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          background: rgba(255, 255, 255, 0.06);
        }
        .demande-conge-container .form-control::placeholder {
          color: #64748b;
        }
        .demande-conge-container .form-select option {
          background: #0b1428;
          color: #e0e7ff;
        }
        .demande-conge-container .form-select:disabled,
        .demande-conge-container .form-control:disabled {
          opacity: 0.5;
        }

        .demande-conge-container .btn-glass {
          padding: 10px 24px;
          border-radius: 30px;
          font-weight: 500;
          transition: all 0.25s ease;
          border: none;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .demande-conge-container .btn-glass-secondary {
          background: rgba(255, 255, 255, 0.04);
          color: #94a3b8;
          border: 1px solid rgba(255, 255, 255, 0.06);
        }
        .demande-conge-container .btn-glass-secondary:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.08);
          color: #e0e7ff;
          transform: translateY(-2px);
        }
        .demande-conge-container .btn-glass-primary {
          background: #3b82f6;
          color: #fff;
          box-shadow: 0 6px 24px rgba(59, 130, 246, 0.2);
        }
        .demande-conge-container .btn-glass-primary:hover:not(:disabled) {
          background: #1d4ed8;
          transform: translateY(-2px);
          box-shadow: 0 8px 32px rgba(59, 130, 246, 0.3);
        }
        .demande-conge-container .btn-glass-primary:disabled {
          opacity: 0.5;
          pointer-events: none;
        }

        .demande-conge-container .alert-glass {
          background: rgba(255, 255, 255, 0.04);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 14px;
          color: #e0e7ff;
          padding: 12px 18px;
        }
        .demande-conge-container .alert-glass-danger {
          border-color: rgba(239, 68, 68, 0.3);
          background: rgba(239, 68, 68, 0.05);
          color: #f87171;
        }
        .demande-conge-container .alert-glass-success {
          border-color: rgba(34, 197, 94, 0.3);
          background: rgba(34, 197, 94, 0.05);
          color: #4ade80;
        }
        .demande-conge-container .alert-glass-warning {
          border-color: rgba(245, 158, 11, 0.3);
          background: rgba(245, 158, 11, 0.05);
          color: #fbbf24;
        }
        .demande-conge-container .btn-close-custom {
          background: transparent;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 6px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .demande-conge-container .btn-close-custom:hover {
          color: #fff;
        }

        .demande-conge-container .spinner-border-sm {
          width: 1rem;
          height: 1rem;
          border-width: 0.15em;
        }

        .demande-conge-container .employe-badge {
          background: rgba(59, 130, 246, 0.12);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 30px;
          padding: 4px 16px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #4ade80;
          font-size: 0.85rem;
          font-weight: 500;
        }

        @media (max-width: 576px) {
          .demande-conge-container {
            padding: 1rem;
          }
          .demande-conge-container .btn-glass {
            width: 100%;
            justify-content: center;
          }
          .demande-conge-container .d-flex.justify-content-end {
            flex-direction: column;
            gap: 8px;
          }
        }
      `}</style>

      <div className="demande-conge-container">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="d-flex align-items-center gap-2 mb-0">
            <FontAwesomeIcon icon={faBriefcase} className="text-primary" />
            <span>Nouvelle demande de congé</span>
          </h5>
          {employeId && (
            <span className="employe-badge">
              <FontAwesomeIcon icon={faCheckCircle} /> ID Employé #{employeId}
            </span>
          )}
        </div>

        {error && (
          <Alert variant="danger" className="alert-glass alert-glass-danger" dismissible onClose={reset}>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" className="alert-glass alert-glass-success" dismissible onClose={reset}>
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            Demande enregistrée avec succès !
          </Alert>
        )}
        {validationError && (
          <Alert variant="warning" className="alert-glass alert-glass-warning" dismissible onClose={() => setValidationError(null)}>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {validationError}
          </Alert>
        )}

        <Form onSubmit={handleSubmit} noValidate>
          {/* Champ caché pour l'employeId */}
          <Form.Control
            type="hidden"
            name="employeId"
            value={formData.employeId}
          />

          <Form.Group className="mb-3" controlId="type">
            <Form.Label>Type de congé</Form.Label>
            <Form.Select
              name="type"
              value={formData.type}
              onChange={handleChange}
              disabled={submitting}
              required
            >
              <option value="">Sélectionner</option>
              <option value="Annuel">Annuel</option>
              <option value="Maladie">Maladie</option>
              <option value="Maternité">Maternité</option>
              <option value="Paternité">Paternité</option>
              <option value="Sans solde">Sans solde</option>
              <option value="Autre">Autre</option>
            </Form.Select>
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3" controlId="dateDebut">
                <Form.Label><FontAwesomeIcon icon={faCalendarAlt} className="me-2" />Date de début</Form.Label>
                <Form.Control
                  type="date"
                  name="dateDebut"
                  value={formData.dateDebut}
                  onChange={handleChange}
                  disabled={submitting}
                  required
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3" controlId="dateFin">
                <Form.Label><FontAwesomeIcon icon={faCalendarAlt} className="me-2" />Date de fin</Form.Label>
                <Form.Control
                  type="date"
                  name="dateFin"
                  value={formData.dateFin}
                  onChange={handleChange}
                  disabled={submitting}
                  required
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3" controlId="motif">
            <Form.Label><FontAwesomeIcon icon={faComment} className="me-2" />Motif</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="motif"
              value={formData.motif}
              onChange={handleChange}
              placeholder="Précisez le motif du congé..."
              disabled={submitting}
              required
            />
          </Form.Group>

          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              type="reset"
              className="btn-glass btn-glass-secondary"
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  type: '',
                  dateDebut: '',
                  dateFin: '',
                  motif: ''
                }));
                reset();
                setValidationError(null);
              }}
              disabled={submitting}
            >
              <FontAwesomeIcon icon={faTimes} className="me-1" /> Annuler
            </Button>
            <Button
              variant="primary"
              type="submit"
              className="btn-glass btn-glass-primary"
              disabled={submitting}
            >
              {submitting ? (
                <Spinner as="span" animation="border" size="sm" className="me-2" />
              ) : (
                <FontAwesomeIcon icon={faSave} className="me-2" />
              )}
              Enregistrer
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
};

export default InfoCongesEmploye;